import type { RouterContextType } from "@/api/permissions/permissions.api";
// SERV-1903: EIN hidden from entity creation — restore alongside the EIN row below
// import { formatEinForDisplay } from "@/components/features/Entity/EditEntityDetailsModal/EditEntityDetailsModal";
import { useEntitiesStore } from "@/store/entitiesStore";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { buildEntityWorkspaceUrl } from "@/utils/entityWorkspaceUrl";
import { Anchor, Box, Flex, Stack, Text, Title, Group } from "@mantine/core";
import { Button, Card, Tooltip } from "@onengine/component-library";
import { IconRosetteDiscountCheckFilled } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import styles from "./success.module.scss";

export const Route = createFileRoute("/entities/create-entity/success")({
  component: EntitySuccess,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

export function EntitySuccess() {
  const navigate = useNavigate();
  const { lastCreatedEntity } = useEntitiesStore();

  const entityId = lastCreatedEntity?.id;
  const subDomain = lastCreatedEntity?.subDomain;
  const entityTypeRaw = lastCreatedEntity?.entityType;
  const category = lastCreatedEntity?.category;
  const entityName = lastCreatedEntity?.entityName;
  // SERV-1903: EIN hidden from entity creation — restore with the EIN row below
  // const ein = lastCreatedEntity?.ein;
  const website = lastCreatedEntity?.website;
  const hqStreet = lastCreatedEntity?.hqStreet;
  const hqCity = lastCreatedEntity?.hqCity;
  const hqState = lastCreatedEntity?.hqState;
  const hqZip = lastCreatedEntity?.hqZip;

  const entityWorkspaceUrl = buildEntityWorkspaceUrl(subDomain ?? "");
  const entityWorkspaceHost = entityWorkspaceUrl.replace(/^https?:\/\//, "");

  const friendlyEntityType =
    entityTypeRaw === "internal"
      ? "Internal"
      : entityTypeRaw === "external"
        ? "External"
        : entityTypeRaw || "Internal";

  const formatWebsite = (website: string) => {
    if (!website) return "";
    const cleaned = website.replace(/^https?:\/\//, "");
    return cleaned;
  };

  const truncateWebsite = (website: string, maxLength: number = 50) => {
    const formatted = formatWebsite(website);
    if (formatted.length <= maxLength) {
      return formatted;
    }
    return `${formatted.slice(0, maxLength)}...`;
  };

  const handleClose = () => {
    navigate({ to: "/entities" });
  };

  return (
    <Box className={styles.container}>
      <Flex direction="column" align="center" className={styles.contentWrapper}>
        <Stack gap="lg" className={styles.contentStack}>
          <Flex direction="column" align="flex-start" gap="md" className={styles.successHeader}>
            <Box className={styles.successIcon}>
              <IconRosetteDiscountCheckFilled size={64} color="var(--icon-success-default)" />
            </Box>
            <Title order={3} className={styles.successTitle}>
              Entity Created Successfully!
            </Title>
          </Flex>

          <Card title="Entity Details">
            <Stack gap="xs" className={styles.detailStack}>
              <Box className={styles.detailRow}>
                <Text className={styles.detailLabel}>Entity Type</Text>
                <Text className={styles.internalCategory}>
                  {friendlyEntityType}{" "}
                  {category && (
                    <>
                      -{" "}
                      <Text component="span" className={styles.internalValue}>
                        {category}
                      </Text>
                    </>
                  )}
                </Text>
              </Box>
              <Box className={styles.detailRow}>
                <Text className={styles.detailLabel}>Entity name</Text>
                <Text className={styles.internalCategory}>{entityName || "--"}</Text>
              </Box>
              {/* SERV-1903: EIN hidden from entity creation — restore by uncommenting
              <Box className={styles.detailSeparator} />
              <Box className={styles.detailRow}>
                <Text className={styles.detailLabel}>EIN</Text>
                <Text className={styles.internalValue}>
                  {formatEinForDisplay(ein ?? "") || "--"}
                </Text>
              </Box>
              */}
              <Box className={styles.detailRow}>
                <Text className={styles.detailLabel}>Website</Text>
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Tooltip
                    content={
                      <Text
                        style={{
                          wordBreak: "break-all",
                          whiteSpace: "normal",
                          maxWidth: "400px",
                        }}
                      >
                        {formatWebsite(website ?? "--")}
                      </Text>
                    }
                    pointer="top-center"
                  >
                    <Anchor
                      href={`https://${formatWebsite(website ?? "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.websiteLink}
                    >
                      {truncateWebsite(website ?? "")}
                    </Anchor>
                  </Tooltip>
                </Stack>
              </Box>
              <Box className={styles.detailSeparator} />

              <Box className={styles.detailRow}>
                <Text className={styles.detailLabel}>Entity HQ Address</Text>
                <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                  <Tooltip
                    content={`${hqStreet ?? "--"}, ${hqCity ?? ""}, ${hqState ?? ""} ${hqZip ?? ""}`}
                    pointer="top-center"
                    size="large"
                  >
                    <Text className={styles.hqAddressValue}>
                      {hqStreet ?? "--"}, {hqCity ?? ""}, {hqState ?? ""} {hqZip ?? ""}
                    </Text>
                  </Tooltip>
                </Stack>
              </Box>
            </Stack>
          </Card>

          <Card title="What's Next">
            <Stack gap="md">
              <Text size="sm">
                <Text component="span" className={styles.whatNextLabel}>
                  Log in
                </Text>{" "}
                to the workspace to manage users, clients, brands, and locations for this entity at{" "}
                <Anchor
                  href={entityWorkspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.whatNextLink}
                >
                  {entityWorkspaceHost}
                </Anchor>
                .
              </Text>
              <Text size="sm">
                <Text component="span" className={styles.whatNextLabel}>
                  Update
                </Text>{" "}
                the entity&apos;s name, address, or settings from <br />
                <Anchor
                  component="button"
                  className={styles.whatNextLink}
                  onClick={() =>
                    navigate({ to: "/entities/$entityId", params: { entityId: entityId ?? "" } })
                  }
                >
                  Edit Entity Details.
                </Anchor>
              </Text>
            </Stack>
          </Card>
          <Box>
            <Group justify="flex-end">
              <Button variant="primary" size="large" onClick={handleClose}>
                {"Back to Entities"}
              </Button>
            </Group>
          </Box>
        </Stack>
      </Flex>
    </Box>
  );
}
